import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { adminAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';
import { toast } from 'sonner';
import { Download } from 'lucide-react';

const statusColor = {
  pending: 'bg-yellow-500',
  confirmed: 'bg-green-600',
  active: 'bg-blue-600',
  completed: 'bg-gray-500',
  rejected: 'bg-red-500',
  cancelled: 'bg-orange-500',
};

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getAllBookings();
        setBookings(res.data || []);
      } catch (e) {
        toast.error('Error cargando reservas');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exportCSV = async () => {
    try {
      const res = await adminAPI.exportCSV('bookings');
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error exportando');
    }
  };

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  return (
    <Card data-testid="admin-bookings-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Reservas ({bookings.length})</CardTitle>
        <Button onClick={exportCSV} variant="outline" size="sm" data-testid="export-bookings-csv-btn">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4">
          {['all', 'pending', 'confirmed', 'active', 'completed', 'rejected', 'cancelled'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 text-xs rounded-full border ${filter === s ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700'}`}
              data-testid={`bookings-filter-${s}`}
            >{s}</button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-3">Auto</th>
                <th className="p-3">Renter</th>
                <th className="p-3">Mobilitier</th>
                <th className="p-3">Fechas</th>
                <th className="p-3">Total</th>
                <th className="p-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id} className="border-t" data-testid={`booking-row-${b.id}`}>
                  <td className="p-3">{b.car_name || '—'}</td>
                  <td className="p-3 text-gray-600">{b.user_name}</td>
                  <td className="p-3 text-gray-600">{b.mobilitier_name}</td>
                  <td className="p-3 text-xs">{(b.start_date || '').split('T')[0]} → {(b.end_date || '').split('T')[0]}</td>
                  <td className="p-3 font-medium">${(b.total_price || 0).toLocaleString()}</td>
                  <td className="p-3"><Badge className={statusColor[b.status] || 'bg-gray-400'}>{b.status}</Badge></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminBookings;
